import { useState, useMemo } from 'react';
import { safeFormat } from '@/lib/safeDate';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { 
  AlertTriangle, 
  Phone, 
  MessageCircle, 
  ClipboardList,
  Users,
  DollarSign,
  Calendar,
  History,
  RefreshCw,
  List,
  Users2,
  CreditCard
} from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { parseDate } from '@/lib/localizedDateUtils';
import { 
  useCollectionDashboard, 
  useVendedoresList,
  type AgingBucket,
  type OverduePayment,
  type PeriodFilter 
} from '@/hooks/finance/useCollectionDashboard';
import { useLastCollectionContacts, getOutcomeLabel } from '@/hooks/finance/useCollectionHistory';
import { useCollectionIndicators } from '@/hooks/finance/useCollectionIndicators';
import { BusinessUnitSelector, type BusinessUnit } from '@/components/finance/BusinessUnitSelector';
import { RegisterContactModal } from '@/components/finance/collection/RegisterContactModal';
import { ClientTimelineModal } from '@/components/finance/collection/ClientTimelineModal';
import { PromisesTab } from '@/components/finance/collection/PromisesTab';
import { RenegotiationApprovalQueue } from '@/components/finance/collection/RenegotiationApprovalQueue';
import { RequestRenegotiationModal } from '@/components/finance/collection/RequestRenegotiationModal';
import { GroupedPaymentsTable, type RenegotiationDealData } from '@/components/finance/collection/GroupedPaymentsTable';
import { AutoChargeTab } from '@/components/finance/collection/AutoChargeTab';
import { FinancePageLayout } from '@/components/finance/FinancePageLayout';

const AGING_BUCKETS: { value: AgingBucket; label: string; color: string }[] = [
  { value: 'all', label: 'Todos', color: 'bg-muted' },
  { value: '1-30', label: '1-30 dias', color: 'bg-yellow-500' },
  { value: '31-60', label: '31-60 dias', color: 'bg-orange-500' },
  { value: '61-90', label: '61-90 dias', color: 'bg-red-500' },
  { value: '90+', label: '90+ dias', color: 'bg-destructive' },
];

const PERIOD_OPTIONS: { value: PeriodFilter; label: string }[] = [
  { value: 'all', label: 'Todos' },
  { value: 'today', label: 'Hoje' },
  { value: 'yesterday', label: 'Ontem' },
  { value: 'this_week', label: 'Esta semana' },
  { value: 'last_week', label: 'Semana passada' },
  { value: 'this_month', label: 'Este mês' },
  { value: 'last_month', label: 'Mês passado' },
  { value: 'due_today', label: 'Vencendo hoje' },
  { value: 'due_this_week', label: 'Vencendo esta semana' },
];

function formatCurrency(value: number, currency: string = 'USD') {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 2,
  }).format(value);
}

function formatPhone(phone: string | null, countryCode: string | null): string {
  if (!phone) return '-';
  const code = countryCode || '';
  return `${code}${phone}`.replace(/\s+/g, '');
}

function getWhatsAppUrl(phone: string | null, countryCode: string | null, clientName: string): string {
  const fullPhone = formatPhone(phone, countryCode).replace(/[^0-9]/g, '');
  if (!fullPhone) return '#';
  const message = encodeURIComponent(`Olá ${clientName}, tudo bem? Estou entrando em contato sobre um pagamento pendente.`);
  return `https://wa.me/${fullPhone}?text=${message}`;
}

function AgingBadge({ dias }: { dias: number }) {
  let variant: 'default' | 'secondary' | 'destructive' | 'outline' = 'secondary';
  let label = '';
  
  if (dias <= 30) {
    variant = 'secondary';
    label = '1-30d';
  } else if (dias <= 60) {
    variant = 'default';
    label = '31-60d';
  } else if (dias <= 90) {
    variant = 'destructive';
    label = '61-90d';
  } else {
    variant = 'destructive';
    label = '90+d';
  }
  
  return (
    <Badge variant={variant} className="font-mono">
      {dias}d ({label})
    </Badge>
  );
}

export default function CollectionDashboard() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const requestedTab = searchParams.get('tab');
  const isValidTab = requestedTab === 'cobranca' || requestedTab === 'automatico' || requestedTab === 'promessas' || requestedTab === 'renegociacoes';
  const [businessUnit, setBusinessUnit] = useState<BusinessUnit>('all');
  const [agingBucket, setAgingBucket] = useState<AgingBucket>('all');
  const [vendedorId, setVendedorId] = useState<string>('');
  const [period, setPeriod] = useState<PeriodFilter>('all');
  const [activeTab, setActiveTab] = useState<'cobranca' | 'automatico' | 'promessas' | 'renegociacoes'>(
    isValidTab ? (requestedTab as 'cobranca' | 'automatico' | 'promessas' | 'renegociacoes') : 'cobranca'
  );
  const [viewMode, setViewMode] = useState<'installments' | 'clients'>('installments');

  // Modal states
  const [registerModalPayment, setRegisterModalPayment] = useState<OverduePayment | null>(null);
  const [timelineModalContact, setTimelineModalContact] = useState<{ id: string; name: string } | null>(null);
  const [renegotiationDeal, setRenegotiationDeal] = useState<RenegotiationDealData | null>(null);

  const { data, isLoading } = useCollectionDashboard({
    businessUnit,
    agingBucket,
    vendedorId: vendedorId || undefined,
    period,
  });

  const { data: vendedores } = useVendedoresList();

  const payments = data?.payments || [];
  const summary = data?.summary;

  // Get payment IDs for fetching last contacts
  const paymentIds = useMemo(() => payments.map(p => p.payment_id), [payments]);
  const { data: lastContacts } = useLastCollectionContacts(paymentIds);

  // Get contact IDs for fetching indicators
  const contactIds = useMemo(() => [...new Set(payments.map(p => p.contact_id))], [payments]);
  const { data: indicators } = useCollectionIndicators(contactIds);

  return (
    <FinancePageLayout>
      
      {/* Header */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <h1 className="text-2xl font-bold tracking-tight">Dashboard de Cobrança</h1>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid w-full max-w-2xl grid-cols-4">
          <TabsTrigger value="cobranca" className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4" />
            Cobrança
          </TabsTrigger>
          <TabsTrigger value="automatico" className="flex items-center gap-2">
            <CreditCard className="h-4 w-4" />
            Automático
          </TabsTrigger>
          <TabsTrigger value="promessas" className="flex items-center gap-2">
            <Calendar className="h-4 w-4" />
            Promessas
          </TabsTrigger>
          <TabsTrigger value="renegociacoes" className="flex items-center gap-2">
            <RefreshCw className="h-4 w-4" />
            Renegociações
          </TabsTrigger>
        </TabsList>

          {/* Tab: Cobrança */}
          <TabsContent value="cobranca" className="space-y-6">
            {/* Summary Cards */}
            <div className="grid gap-4 md:grid-cols-4">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Total em Atraso</CardTitle>
                  <DollarSign className="h-4 w-4 text-destructive" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-destructive">
                    {formatCurrency(summary?.totalOverdue || 0)}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {payments.length} parcelas
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Clientes em Atraso</CardTitle>
                  <Users className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">
                    {summary?.totalClients || 0}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    clientes únicos
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Maior Atraso</CardTitle>
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">
                    {summary?.maxDaysOverdue || 0} dias
                  </div>
                  <p className="text-xs text-muted-foreground">
                    pagamento mais antigo
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">90+ dias</CardTitle>
                  <AlertTriangle className="h-4 w-4 text-destructive" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-destructive">
                    {formatCurrency(summary?.byBucket['90+'].amount || 0)}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {summary?.byBucket['90+'].count || 0} parcelas críticas
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Aging Bucket Cards */}
            <div className="grid gap-2 md:grid-cols-4">
              {AGING_BUCKETS.filter(b => b.value !== 'all').map(bucket => {
                const bucketData = summary?.byBucket[bucket.value as keyof typeof summary.byBucket];
                const isSelected = agingBucket === bucket.value;
                
                return (
                  <Card 
                    key={bucket.value}
                    className={`cursor-pointer transition-all hover:shadow-md ${
                      isSelected ? 'ring-2 ring-primary' : ''
                    }`}
                    onClick={() => setAgingBucket(isSelected ? 'all' : bucket.value)}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium">{bucket.label}</p>
                          <p className="text-lg font-bold">
                            {formatCurrency(bucketData?.amount || 0)}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {bucketData?.count || 0} parcelas
                          </p>
                        </div>
                        <div className={`h-3 w-3 rounded-full ${bucket.color}`} />
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {/* Filters */}
            <Card>
              <CardContent className="p-4">
                <div className="flex flex-wrap gap-4">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium">BU:</span>
                    <BusinessUnitSelector 
                      value={businessUnit} 
                      onChange={setBusinessUnit} 
                    />
                  </div>

                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium">Closer:</span>
                    <Select 
                      value={vendedorId || '__all__'} 
                      onValueChange={(v) => setVendedorId(v === '__all__' ? '' : v)}
                    >
                      <SelectTrigger className="w-[180px]">
                        <SelectValue placeholder="Todos" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__all__">Todos</SelectItem>
                        {vendedores?.map(v => (
                          <SelectItem key={v.id} value={v.id}>
                            {v.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium">Faixa:</span>
                    <Select value={agingBucket} onValueChange={(v) => setAgingBucket(v as AgingBucket)}>
                      <SelectTrigger className="w-[140px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {AGING_BUCKETS.map(bucket => (
                          <SelectItem key={bucket.value} value={bucket.value}>
                            {bucket.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium">Período:</span>
                    <Select value={period} onValueChange={(v) => setPeriod(v as PeriodFilter)}>
                      <SelectTrigger className="w-[180px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {PERIOD_OPTIONS.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {(businessUnit !== 'all' || vendedorId || agingBucket !== 'all' || period !== 'all') && (
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => {
                        setBusinessUnit('all');
                        setVendedorId('');
                        setAgingBucket('all');
                        setPeriod('all');
                      }}
                    >
                      Limpar filtros
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Payments Table */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <span>Pagamentos em Atraso</span>
                    <Badge variant="outline">{payments.length} registros</Badge>
                  </CardTitle>
                  <div className="flex items-center gap-2">
                    <Button
                      variant={viewMode === 'installments' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setViewMode('installments')}
                      className="flex items-center gap-2"
                    >
                      <List className="h-4 w-4" />
                      Por Parcela
                    </Button>
                    <Button
                      variant={viewMode === 'clients' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setViewMode('clients')}
                      className="flex items-center gap-2"
                    >
                      <Users2 className="h-4 w-4" />
                      Por Cliente
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {isLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                  </div>
                ) : payments.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    Nenhum pagamento em atraso encontrado com os filtros selecionados.
                  </div>
                ) : viewMode === 'clients' ? (
                  <GroupedPaymentsTable
                    payments={payments}
                    indicators={indicators}
                    onOpenTimeline={setTimelineModalContact}
                    onRegisterContact={setRegisterModalPayment}
                    onRequestRenegotiation={setRenegotiationDeal}
                  />
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Cliente</TableHead>
                          <TableHead>Indicadores</TableHead>
                          <TableHead>Telefone</TableHead>
                          <TableHead>Valor Aberto</TableHead>
                          <TableHead>Vencimento</TableHead>
                          <TableHead>Atraso</TableHead>
                          <TableHead>Último Contato</TableHead>
                          <TableHead>Closer</TableHead>
                          <TableHead className="text-right">Ações</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {payments.map((payment) => {
                          const lastContact = lastContacts?.[payment.payment_id];
                          const clientIndicators = indicators?.[payment.contact_id];
                          
                          return (
                            <TableRow key={payment.payment_id}>
                              <TableCell>
                                <div className="flex flex-col">
                                  <button 
                                    onClick={() => setTimelineModalContact({ 
                                      id: payment.contact_id, 
                                      name: payment.cliente 
                                    })}
                                    className="font-medium hover:underline flex items-center gap-1 text-left"
                                  >
                                    {payment.cliente}
                                    <History className="h-3 w-3 text-muted-foreground" />
                                  </button>
                                  <span className="text-xs text-muted-foreground">
                                    {payment.description}
                                  </span>
                                </div>
                              </TableCell>
                              <TableCell>
                                <TooltipProvider>
                                  <div className="flex items-center gap-1">
                                    {clientIndicators?.hasApprovedRenegotiation && (
                                      <Tooltip>
                                        <TooltipTrigger>
                                          <Badge variant="secondary" className="text-xs px-1.5 py-0.5 bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300">
                                            🔄
                                          </Badge>
                                        </TooltipTrigger>
                                        <TooltipContent>
                                          <p>Cliente já teve renegociação aprovada</p>
                                        </TooltipContent>
                                      </Tooltip>
                                    )}
                                    {clientIndicators?.activePromise && (
                                      <Tooltip>
                                        <TooltipTrigger>
                                          <Badge variant="secondary" className="text-xs px-1.5 py-0.5 bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300">
                                            📅 {safeFormat(new Date(clientIndicators.activePromise.date), 'dd/MM')}
                                          </Badge>
                                        </TooltipTrigger>
                                        <TooltipContent>
                                          <p>Promessa de pagamento em {safeFormat(new Date(clientIndicators.activePromise.date), 'dd/MM/yyyy', { locale: ptBR })}</p>
                                        </TooltipContent>
                                      </Tooltip>
                                    )}
                                    {clientIndicators?.brokenPromiseCount && clientIndicators.brokenPromiseCount > 0 && (
                                      <Tooltip>
                                        <TooltipTrigger>
                                          <Badge variant="secondary" className="text-xs px-1.5 py-0.5 bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300">
                                            ⚠️ {clientIndicators.brokenPromiseCount}
                                          </Badge>
                                        </TooltipTrigger>
                                        <TooltipContent>
                                          <p>Quebrou {clientIndicators.brokenPromiseCount} promessa(s) anteriormente</p>
                                        </TooltipContent>
                                      </Tooltip>
                                    )}
                                    {!clientIndicators?.hasApprovedRenegotiation && 
                                     !clientIndicators?.activePromise && 
                                     !clientIndicators?.brokenPromiseCount && (
                                      <span className="text-muted-foreground text-xs">-</span>
                                    )}
                                  </div>
                                </TooltipProvider>
                              </TableCell>
                              <TableCell>
                                <span className="font-mono text-sm">
                                  {formatPhone(payment.primary_phone, payment.primary_phone_country_code)}
                                </span>
                              </TableCell>
                              <TableCell>
                                <span className="font-medium text-destructive">
                                  {formatCurrency(payment.valor_aberto, payment.currency)}
                                </span>
                              </TableCell>
                              <TableCell>
                                {safeFormat(parseDate(payment.due_date)!, 'dd/MM/yyyy', { locale: ptBR })}
                              </TableCell>
                              <TableCell>
                                <AgingBadge dias={payment.dias_atraso} />
                              </TableCell>
                              <TableCell>
                                {lastContact ? (
                                  <div className="flex flex-col">
                                    <span className="text-xs">
                                      {safeFormat(new Date(lastContact.contact_date), 'dd/MM', { locale: ptBR })}
                                    </span>
                                    <Badge variant="outline" className="text-xs w-fit">
                                      {getOutcomeLabel(lastContact.outcome)}
                                    </Badge>
                                  </div>
                                ) : (
                                  <span className="text-muted-foreground text-sm">-</span>
                                )}
                              </TableCell>
                              <TableCell>
                                <span className="text-sm">{payment.vendedor || '-'}</span>
                              </TableCell>
                              <TableCell>
                                <div className="flex items-center justify-end gap-1">
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    asChild
                                    title="WhatsApp"
                                  >
                                    <a
                                      href={getWhatsAppUrl(payment.primary_phone, payment.primary_phone_country_code, payment.cliente)}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                    >
                                      <MessageCircle className="h-4 w-4 text-green-600" />
                                    </a>
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    asChild
                                    title="Ligar"
                                  >
                                    <a href={`tel:${formatPhone(payment.primary_phone, payment.primary_phone_country_code)}`}>
                                      <Phone className="h-4 w-4 text-blue-600" />
                                    </a>
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    title="Registrar Contato"
                                    onClick={() => setRegisterModalPayment(payment)}
                                  >
                                    <ClipboardList className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    title="Solicitar Renegociação"
                                    onClick={() => {
                                      // Para a visão por parcela, agrupamos pelo deal também
                                      const dealPayments = payments.filter(p => p.deal_id === payment.deal_id);
                                      const totalOverdue = dealPayments.reduce((sum, p) => sum + (p.valor_aberto || 0), 0);
                                      const dealName = payment.description?.split(' - ')?.[0] || 'Contrato';
                                      setRenegotiationDeal({
                                        dealId: payment.deal_id,
                                        dealName,
                                        contactId: payment.contact_id,
                                        clientName: payment.cliente,
                                        totalOverdue,
                                        paymentCount: dealPayments.length,
                                        currency: payment.currency,
                                      });
                                    }}
                                  >
                                    <RefreshCw className="h-4 w-4 text-orange-600" />
                                  </Button>
                                </div>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tab: Automático */}
          <TabsContent value="automatico">
            <AutoChargeTab />
          </TabsContent>

          {/* Tab: Promessas */}
          <TabsContent value="promessas">
            <PromisesTab />
          </TabsContent>

          {/* Tab: Renegociações */}
          <TabsContent value="renegociacoes">
            <RenegotiationApprovalQueue />
          </TabsContent>
        </Tabs>

      {/* Register Contact Modal */}
      {registerModalPayment && (
        <RegisterContactModal
          open={!!registerModalPayment}
          onClose={() => setRegisterModalPayment(null)}
          paymentId={registerModalPayment.payment_id}
          contactId={registerModalPayment.contact_id}
          clientName={registerModalPayment.cliente}
        />
      )}

      {/* Client Timeline Modal */}
      {timelineModalContact && (
        <ClientTimelineModal
          open={!!timelineModalContact}
          onClose={() => setTimelineModalContact(null)}
          contactId={timelineModalContact.id}
          clientName={timelineModalContact.name}
        />
      )}

      {/* Request Renegotiation Modal */}
      {renegotiationDeal && (
        <RequestRenegotiationModal
          open={!!renegotiationDeal}
          onClose={() => setRenegotiationDeal(null)}
          dealId={renegotiationDeal.dealId}
          clientName={`${renegotiationDeal.clientName} - ${renegotiationDeal.dealName}`}
          currentBalance={renegotiationDeal.totalOverdue}
          currentInstallments={renegotiationDeal.paymentCount}
        />
      )}
    </FinancePageLayout>
  );
}
